import { useEffect, useState } from "react";
import { PoweredBy } from "@/components/candidate/CandidateBrand";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Video, Code2, ShieldCheck } from "lucide-react";
import PhoneStatusNotice from "@/components/candidate/PhoneStatusNotice";

// Ordered list of IN-BROWSER stages a candidate walks through, in order.
// Telephonic is intentionally excluded — it is conducted over a real phone call
// initiated by the recruiter, not via the browser invite link.
const STAGE_SEQ = ["video", "coding"];
const STAGE_ROUTES = { video: "video", coding: "coding" };

function enabledStageSequence(job) {
  const s = job?.stages || { video: true, coding: true };
  return STAGE_SEQ.filter((k) => s[k]);
}

// Next route for a candidate given their current stage field. Honors "finish
// whatever you're on, then skip disabled stages".
export function nextRouteForCandidate(token, candidate, job) {
  const seq = enabledStageSequence(job);
  if (seq.length === 0) return `/invite/${token}/done`;
  const stage = candidate?.stage || "invited";
  if (["invited", "screening", "phone_call"].includes(stage)) {
    return `/invite/${token}/${STAGE_ROUTES[seq[0]]}`;
  }
  if (seq.includes(stage)) {
    return `/invite/${token}/${STAGE_ROUTES[stage]}`;
  }
  return `/invite/${token}/done`;
}

export default function InvitePage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);

  useEffect(() => {
    api.get(`/invite/${token}`).then(r => setInv(r.data)).catch(() => setInv(false));
  }, [token]);

  const start = async () => {
    if (!inv.candidate.consent_given) {
      try { await api.post(`/invite/${token}/consent`); }
      catch (e) { toast.error(fmtErr(e.response?.data?.detail)); return; }
    }
    nav(nextRouteForCandidate(token, { ...inv.candidate, stage: "screening" }, inv.job));
  };

  if (inv === null) return <div className="tp-portal grid place-items-center p-6"><div className="text-slate-500">Loading…</div></div>;
  if (inv === false) return <div className="tp-portal grid place-items-center p-6"><div className="tp-card p-8 max-w-md text-center"><div className="font-display text-xl mb-2">Invalid invite</div><div className="text-slate-500 text-sm">This link is invalid or expired. Please contact the recruiter.</div></div></div>;

  const { candidate, job, tenant } = inv;
  const currentStage = candidate.stage;
  const seq = enabledStageSequence(job);
  const telephonicEnabled = !!job?.stages?.telephonic;
  const STAGE_META = {
    video: { Icon: Video, title: "AI Video Interview", desc: "A real conversation on camera with Ava, our AI interviewer. Your webcam and microphone stay on throughout — Ava speaks each question aloud and follows up like a human recruiter would." },
    coding: { Icon: Code2, title: "Coding Challenge", desc: "A coding problem in your favorite language. Your camera stays on during coding as a proctor signal. Real execution, instant feedback, one optional hint." },
  };

  // Rough duration estimates per stage (minutes). Telephonic is over phone, not here.
  const DURATION = { video: [12, 18], coding: [15, 25] };
  const minT = seq.reduce((s, k) => s + DURATION[k][0], 0);
  const maxT = seq.reduce((s, k) => s + DURATION[k][1], 0);
  const stageWord = seq.length === 1 ? "one short stage" : seq.length === 2 ? "two short stages" : "a short set of stages";

  const goNext = () => nav(nextRouteForCandidate(token, candidate, job));

  return (
    <div className="tp-portal">
      {tenant && (
        <div className="max-w-3xl mx-auto px-6 pt-8 flex items-center gap-3" data-testid="portal-brand">
          {tenant.logo_data_url ? (
            <img src={tenant.logo_data_url} alt={tenant.name} className="h-10 max-w-[180px] object-contain" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-indigo-600 grid place-items-center text-white font-bold text-lg">{(tenant.name || "T")[0]}</div>
          )}
          <div className="font-display font-bold text-lg text-slate-900">{tenant.name}</div>
        </div>
      )}
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="tp-chip mb-4" style={{color: "#4338CA", borderColor: "rgba(99,102,241,0.35)", background: "rgba(99,102,241,0.10)"}}>Invitation</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">Hi {candidate.name}, welcome.</h1>
        <p className="text-slate-600 text-base sm:text-lg mb-8 leading-relaxed max-w-xl">
          You're invited to interview for <span className="font-semibold text-slate-900">{job.title}</span>{tenant?.name ? <> at <span className="font-semibold text-slate-900">{tenant.name}</span></> : null}. The full experience runs in this browser and takes about {minT}–{maxT} minutes across {stageWord}.
        </p>

        <div className="tp-card p-6 mb-6" data-testid="stage-list">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-4">What to expect</div>
          {telephonicEnabled && <PhoneStatusNotice candidate={candidate} />}
          <div className="space-y-4">
            {seq.map((k, idx) => {
              const { Icon, title, desc } = STAGE_META[k];
              return (
                <div className="flex items-start gap-4" key={k} data-testid={`stage-item-${k}`}>
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 border border-indigo-100 grid place-items-center flex-shrink-0"><Icon className="w-5 h-5 text-indigo-600"/></div>
                  <div>
                    <div className="font-semibold text-slate-900">Step {idx + 1} · {title}</div>
                    <div className="text-sm text-slate-500 mt-0.5">{desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="tp-card p-6 mb-6" data-testid="consent-card">
          <div className="flex items-start gap-3 mb-3">
            <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-slate-900">Consent &amp; recording</div>
              <div className="text-sm text-slate-500 leading-relaxed mt-1">By continuing, you consent to your voice, video, and code being recorded and evaluated for this role. Data is retained per the company's policy and used only for hiring decisions. You can withdraw at any time.</div>
            </div>
          </div>
        </div>

        {["invited"].includes(currentStage) ? (
          <button data-testid="start-interview" onClick={start} className="px-6 py-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors">
            I consent — Start interview
          </button>
        ) : (
          <button data-testid="resume-interview" onClick={goNext} className="px-6 py-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors">
            Continue where you left off
          </button>
        )}
      </div>
      <PoweredBy light />
    </div>
  );
}
